import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { CLINIC } from "@/constants/testIds";

const FIELD_OPTIONS = [
  { value: "email", label: "Email" },
  { value: "date_of_birth", label: "Fecha de nacimiento" },
  { value: "emergency_contact", label: "Contacto de emergencia" },
  { value: "allergies", label: "Alergias / observaciones médicas" },
];

/** Card de Ajustes: campos obligatorios al crear un paciente (panel, reserva
 * publica y agente de WhatsApp). Nombre y telefono son siempre obligatorios. */
export default function RequiredPatientFieldsCard({ clinic, onUpdated }) {
  const [selected, setSelected] = useState(clinic.required_patient_fields || []);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const toggle = (value) => {
    setSelected((s) => (s.includes(value) ? s.filter((v) => v !== value) : [...s, value]));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/clinic", { required_patient_fields: selected });
      onUpdated(data);
      toast({ title: "Campos obligatorios actualizados" });
    } catch (err) {
      toast({ title: "Error al guardar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-border rounded-xl p-6 sm:p-8 shadow-sm space-y-5">
      <div>
        <h2 className="font-serif text-xl text-foreground">Campos obligatorios de pacientes</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Nombre y teléfono son siempre obligatorios. Elegí qué otros datos exigir al crear un paciente: se valida en el panel, la reserva pública y el agente de WhatsApp (que solo pregunta el email por chat; el resto queda marcado como pendiente para completar en la consulta).
        </p>
      </div>
      <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-not-allowed">
        <Checkbox checked disabled />
        Nombre y teléfono (siempre obligatorios)
      </label>
      <div className="space-y-3">
        {FIELD_OPTIONS.map((opt) => (
          <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={selected.includes(opt.value)}
              onCheckedChange={() => toggle(opt.value)}
              data-testid={`${CLINIC.requiredFieldCheckboxPrefix}${opt.value}`}
            />
            {opt.label}
          </label>
        ))}
      </div>
      <Button onClick={handleSave} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid={CLINIC.requiredFieldsSaveButton}>
        {saving ? "Guardando..." : "Guardar cambios"}
      </Button>
    </div>
  );
}
